import { useState, useEffect } from 'react'
import { analyzeSocionics } from '../utils/api'
import { getModelPref } from '../utils/modelPrefs'
import ModelToggle from './ModelToggle'
import InfoTooltip from './InfoTooltip'
import { ASPECT_INFO, POSITION_INFO, QUADRA_INFO, TYPE_INFO } from '../utils/socionicsGlossary'

function useIsMobile(bp = 640) {
  const [v, setV] = useState(() => window.innerWidth < bp)
  useEffect(() => {
    let t; const h = () => { clearTimeout(t); t = setTimeout(() => setV(window.innerWidth < bp), 80) }
    window.addEventListener('resize', h); return () => { clearTimeout(t); window.removeEventListener('resize', h) }
  }, [bp]); return v
}
import { C, font } from './ProfileResult'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://92.53.107.15'

const glass = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: '20px 24px',
  marginBottom: 16,
}

const QUADRA_COLOR = {
  'Альфа': 'rgba(120,180,255,0.15)',
  'Бета':  'rgba(255,120,120,0.15)',
  'Гамма': 'rgba(120,220,150,0.15)',
  'Дельта':'rgba(220,180,100,0.15)',
}
const QUADRA_BORDER = {
  'Альфа': 'rgba(120,180,255,0.35)',
  'Бета':  'rgba(255,120,120,0.35)',
  'Гамма': 'rgba(120,220,150,0.35)',
  'Дельта':'rgba(220,180,100,0.35)',
}

const FUNCTION_COLOR = {
  1: 'rgba(184,189,212,0.9)',  // базовая
  2: 'rgba(150,200,255,0.8)',  // творческая
  3: 'rgba(180,180,180,0.6)',  // ролевая
  4: 'rgba(220,90,90,0.7)',    // болевая
  5: 'rgba(100,180,130,0.7)',  // суггестивная
  6: 'rgba(100,180,130,0.5)',  // мобилизующая
  7: 'rgba(180,180,180,0.4)',  // игнорируемая
  8: 'rgba(184,189,212,0.5)',  // демонстративная
}

const RELATION_COLOR = {
  'Дуальные':          'rgba(77,200,138,0.15)',
  'Активационные':     'rgba(100,180,255,0.12)',
  'Зеркальные':        'rgba(184,189,212,0.12)',
  'Тождественные':     'rgba(184,189,212,0.10)',
  'Деловые':           'rgba(220,180,100,0.12)',
  'Миражные':          'rgba(220,180,100,0.10)',
  'Полудуальные':      'rgba(77,200,138,0.08)',
  'Конфликтные':       'rgba(220,80,80,0.14)',
  'Суперэго':          'rgba(220,80,80,0.10)',
  'Ревизор':           'rgba(220,140,60,0.14)',
  'Подревизный':       'rgba(220,140,60,0.10)',
  'Квазитождественные':'rgba(184,189,212,0.08)',
  'Родственные':       'rgba(180,180,220,0.12)',
  'Заказчик':          'rgba(200,150,255,0.12)',
  'Исполнитель':       'rgba(200,150,255,0.08)',
}
const RELATION_BORDER = {
  'Дуальные':          'rgba(77,200,138,0.5)',
  'Конфликтные':       'rgba(220,80,80,0.5)',
  'Суперэго':          'rgba(220,80,80,0.35)',
  'Ревизор':           'rgba(220,140,60,0.45)',
  'Подревизный':       'rgba(220,140,60,0.3)',
  'Активационные':     'rgba(100,180,255,0.4)',
}

const RELATION_ORDER = [
  'Дуальные','Активационные','Зеркальные','Тождественные',
  'Полудуальные','Деловые','Миражные','Родственные',
  'Заказчик','Исполнитель','Суперэго','Конфликтные',
  'Ревизор','Подревизный','Квазитождественные',
]

function Label({ children, color, borderColor }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px', borderRadius: 4,
      fontFamily: font.mono, fontSize: 10,
      background: color || 'rgba(184,189,212,0.1)',
      border: `1px solid ${borderColor || C.border}`,
      color: C.accent,
    }}>{children}</span>
  )
}

function ConfidenceBadge({ value }) {
  const color = value >= 70 ? 'rgba(77,200,138,0.8)' : value >= 50 ? 'rgba(220,180,100,0.8)' : 'rgba(184,189,212,0.6)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2, maxWidth: 120 }}>
        <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
      <span style={{ fontFamily: font.mono, fontSize: 10, color }}>{value}% уверенность</span>
    </div>
  )
}

export default function SocionicsTab({ userId = 'user', isOwner = false }) {
  const isMobile = useIsMobile()
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError]     = useState(null)
  const [expandedRel, setExpandedRel] = useState(null)

  useEffect(() => {
    fetch(`${BASE_URL}/api/socionics/${userId}`)
      .then(r => r.json())
      .then(d => {
        if (d.empty || d.error) setData(null)
        else setData(d)
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [userId])

  const analyze = async () => {
    setAnalyzing(true); setError(null)
    try {
      const d = await analyzeSocionics(userId, getModelPref('socionics'))
      setData(d)
    } catch (e) {
      setError(e.message)
    } finally {
      setAnalyzing(false)
    }
  }

  if (loading) {
    return <div style={{ padding: '60px 0', textAlign: 'center', fontFamily: font.mono, fontSize: 11, color: C.textDim }}>Загрузка...</div>
  }

  // Empty state
  if (!data) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontFamily: font.mono, fontSize: 32, color: C.textDim, marginBottom: 16, opacity: 0.3 }}>⬡</div>
        <div style={{ fontFamily: font.heading, fontSize: 24, color: C.text, marginBottom: 8, fontWeight: 400 }}>Соционический тип не определён</div>
        <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, marginBottom: 24, maxWidth: 360, margin: '0 auto 24px' }}>
          Claude проанализирует твои тексты и психологический профиль для определения типа
        </p>
        {isOwner && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <button onClick={analyze} disabled={analyzing} style={{
              padding: '10px 28px', borderRadius: 8,
              fontFamily: font.body, fontSize: 13,
              background: 'rgba(184,189,212,0.12)', border: `1px solid ${C.borderHi}`,
              color: C.accent, cursor: analyzing ? 'default' : 'pointer',
            }}>
              {analyzing ? '⏳ Анализирую...' : '⬡ Определить тип'}
            </button>
            <ModelToggle prefKey="socionics" />
          </div>
        )}
        {error && <p style={{ color: 'rgba(220,90,90,0.9)', fontFamily: font.body, fontSize: 13, marginTop: 16 }}>{error}</p>}
      </div>
    )
  }

  const qColor  = QUADRA_COLOR[data.quadra]  || 'rgba(184,189,212,0.1)'
  const qBorder = QUADRA_BORDER[data.quadra] || C.border

  const sortedRelations = [...(data.intertype_relations || [])].sort(
    (a, b) => RELATION_ORDER.indexOf(a.relation) - RELATION_ORDER.indexOf(b.relation)
  )

  return (
    <div style={{ paddingBottom: 60 }}>

      {/* Type card */}
      <div style={{ ...glass, background: qColor, borderColor: qBorder, marginBottom: 24 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 10 }}>
              Соционический тип
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontFamily: font.heading, fontSize: 42, fontWeight: 300, color: C.silverHi, lineHeight: 1 }}>
                {data.pseudonym}
              </span>
              <InfoTooltip
                label={<span style={{ fontFamily: font.mono, fontSize: 16, color: C.accent }}>{data.type_name_ru} / {data.type_code}</span>}
                title={TYPE_INFO[data.type_code]?.decode || data.type_name_ru}
              >
                {TYPE_INFO[data.type_code]?.text || 'Расшифровка недоступна.'}
              </InfoTooltip>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <InfoTooltip
                label={<Label>{data.quadra}-квадра</Label>}
                title={QUADRA_INFO[data.quadra]?.title || `${data.quadra}-квадра`}
              >
                {QUADRA_INFO[data.quadra]?.text || 'Описание недоступно.'}
              </InfoTooltip>
              <Label>{data.club}</Label>
              <Label>{data.temperament}</Label>
              {data.dichotomies && (
                <Label>{Object.values(data.dichotomies).join('')}</Label>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 200 }}>
            <ConfidenceBadge value={data.confidence || 0} />
            {isOwner && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={analyze} disabled={analyzing} style={{
                  padding: '6px 16px', borderRadius: 6,
                  fontFamily: font.mono, fontSize: 10,
                  background: 'transparent', border: `1px solid ${C.border}`,
                  color: C.textMuted, cursor: analyzing ? 'default' : 'pointer',
                }}>
                  {analyzing ? '⏳ пересчёт...' : '↻ пересчитать'}
                </button>
                <ModelToggle prefKey="socionics" />
              </div>
            )}
          </div>
        </div>

        {data.reasoning && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${qBorder}`, fontFamily: font.body, fontSize: 13, color: C.textMuted, lineHeight: 1.65 }}>
            {data.reasoning}
          </div>
        )}
      </div>

      {/* Function stack */}
      <div style={glass}>
        <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 16 }}>
          Модель А — стек функций
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {(data.functions || []).map(fn => (
            <div key={fn.position} style={{
              padding: '12px 16px', borderRadius: 8,
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${C.border}`,
              borderLeft: `3px solid ${FUNCTION_COLOR[fn.position] || C.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <InfoTooltip
                  label={<span style={{ fontFamily: font.mono, fontSize: 18, fontWeight: 700, color: FUNCTION_COLOR[fn.position], minWidth: 24, lineHeight: 1, display: 'inline-block' }}>{fn.position}</span>}
                  title={POSITION_INFO[fn.position]?.name || fn.name}
                >
                  {POSITION_INFO[fn.position]?.text || 'Описание позиции недоступно.'}
                </InfoTooltip>
                <div>
                  <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    {fn.name}
                  </div>
                  <InfoTooltip
                    label={
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                        <span style={{ fontFamily: font.mono, fontSize: 13, color: C.accent }}>{fn.aspect}</span>
                        <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textDim }}>/ {fn.aspect_en}</span>
                      </div>
                    }
                    title={ASPECT_INFO[fn.aspect_en]?.ru || fn.aspect}
                  >
                    {ASPECT_INFO[fn.aspect_en]?.text || 'Описание аспекта недоступно.'}
                  </InfoTooltip>
                </div>
              </div>
              {fn.description && (
                <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.55 }}>
                  {fn.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Profile match/mismatch */}
      {((data.profile_matches?.length > 0) || (data.profile_differences?.length > 0)) && (
        <div style={glass}>
          <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 16 }}>
            Сопоставление с профилем
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            {data.profile_matches?.length > 0 && (
              <div>
                <div style={{ fontFamily: font.mono, fontSize: 10, color: 'rgba(77,200,138,0.8)', marginBottom: 10, letterSpacing: '0.08em' }}>
                  ✓ СОВПАДЕНИЯ
                </div>
                {data.profile_matches.map((m, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10, marginBottom: 8,
                    padding: '8px 12px', borderRadius: 6,
                    background: 'rgba(77,200,138,0.05)',
                    border: '1px solid rgba(77,200,138,0.15)',
                  }}>
                    <span style={{ color: 'rgba(77,200,138,0.7)', flexShrink: 0, fontFamily: font.mono, fontSize: 12 }}>✓</span>
                    <span style={{ fontFamily: font.body, fontSize: 12, color: C.text, lineHeight: 1.5 }}>{m}</span>
                  </div>
                ))}
              </div>
            )}
            {data.profile_differences?.length > 0 && (
              <div>
                <div style={{ fontFamily: font.mono, fontSize: 10, color: 'rgba(220,140,60,0.8)', marginBottom: 10, letterSpacing: '0.08em' }}>
                  △ РАСХОЖДЕНИЯ
                </div>
                {data.profile_differences.map((d, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10, marginBottom: 8,
                    padding: '8px 12px', borderRadius: 6,
                    background: 'rgba(220,140,60,0.05)',
                    border: '1px solid rgba(220,140,60,0.15)',
                  }}>
                    <span style={{ color: 'rgba(220,140,60,0.7)', flexShrink: 0, fontFamily: font.mono, fontSize: 12 }}>△</span>
                    <span style={{ fontFamily: font.body, fontSize: 12, color: C.text, lineHeight: 1.5 }}>{d}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Intertype relations */}
      {sortedRelations.length > 0 && (
        <div style={glass}>
          <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 16 }}>
            Интертипные отношения
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
            {sortedRelations.map((rel, i) => {
              const bg  = RELATION_COLOR[rel.relation]  || 'rgba(184,189,212,0.08)'
              const brd = RELATION_BORDER[rel.relation] || C.border
              const isExpanded = expandedRel === i
              return (
                <div
                  key={i}
                  onClick={() => setExpandedRel(isExpanded ? null : i)}
                  style={{
                    padding: '10px 14px', borderRadius: 8,
                    background: bg, border: `1px solid ${brd}`,
                    cursor: 'pointer', transition: 'all 0.15s',
                    gridColumn: isExpanded ? '1 / -1' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, marginBottom: 3 }}>
                        {rel.relation}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                        <span style={{ fontFamily: font.body, fontSize: 13, color: C.text }}>{rel.pseudonym}</span>
                        <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted }}>{rel.type_name_ru} / {rel.type_code}</span>
                      </div>
                    </div>
                    <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                  {isExpanded && rel.description && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${brd}`, fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>
                      {rel.description}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(212,90,90,0.08)', border: '1px solid rgba(212,90,90,0.25)', color: 'rgba(220,100,100,0.9)', fontFamily: font.body, fontSize: 13 }}>
          {error}
        </div>
      )}
    </div>
  )
}
